import { Autocomplete as MUIAutocomplete, FormHelperText, TextField } from '@mui/material';
import { Controller, ControllerProps, FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { ErrorWidget } from 'app/agriculture/error_widget';
import { AutocompleteChip } from 'app/agriculture/forms/components/Chip/AutocompleteChip';
import './Autocomplete.scss';
import { ReactElement } from 'react';
import classNames from 'classnames';

export interface AutocompleteOption {
    id: string | number;
    name: string;
    icon?: ReactElement;
}

interface AutocompleteProps<FormType extends FieldValues> {
    fieldName: Path<FormType>;
    options: AutocompleteOption[];
    noun: string;
    formController: UseFormReturn<FormType>;
    className?: string;
    rules?: ControllerProps<FormType>['rules'];
}

// eslint-disable-next-line max-lines-per-function
export function Autocomplete<FormType extends FieldValues>({
    fieldName,
    options,
    noun,
    formController,
    className,
    rules,
}: AutocompleteProps<FormType>) {
    const {
        getValues,
        control,
        formState: { errors },
        trigger,
    } = formController;

    const isMultiple = Array.isArray(getValues(fieldName));

    return (
        <Controller
            name={fieldName}
            control={control}
            rules={rules}
            render={({ field: { ref, value, ...field } }) => (
                <div className="Autocomplete" data-testid="Autocomplete">
                    <MUIAutocomplete
                        className={classNames(className)}
                        {...field}
                        fullWidth
                        multiple={isMultiple}
                        value={
                            isMultiple
                                ? options.filter((options) =>
                                      (value as (string | number)[]).some((j) => j == options.id),
                                  )
                                : (options.find((o) => o.id === value) ?? null)
                        }
                        id={`entity-${fieldName}-input`}
                        data-testid={`entity-${fieldName}-input`}
                        classes={{
                            input: 'Autocomplete__input',
                            inputRoot: 'Autocomplete__input-root',
                            listbox: 'Autocomplete__listbox',
                            option: 'Autocomplete__option',
                        }}
                        options={options}
                        getOptionLabel={({ name }: AutocompleteOption) => name}
                        renderTags={(options: AutocompleteOption[]) =>
                            options.map(({ name, icon, id }: AutocompleteOption) => (
                                <AutocompleteChip
                                    key={id}
                                    label={name}
                                    icon={icon}
                                    onDelete={() => {
                                        const filteredValues = options.filter(
                                            ({ id: itemId }) => itemId != id,
                                        );
                                        field.onChange(filteredValues);
                                        trigger().catch((e: unknown) => {
                                            console.error(e);
                                        });
                                    }}
                                />
                            ))
                        }
                        // This is a workaround to get the id to save/retrieve to the form, seems like an issue with mui and react-hook-form
                        // https://stackoverflow.com/questions/61655199/proper-way-to-use-react-hook-form-controller-with-material-ui-autocomplete
                        onChange={(
                            e,
                            data:
                                | { id: string | number; name: string }
                                | { id: string | number; name: string }[]
                                | null,
                        ) => {
                            if (Array.isArray(data)) {
                                if (data.length > 0) {
                                    field.onChange(data.map((x) => x.id));
                                } else {
                                    field.onChange([]);
                                }
                            } else {
                                if (data?.id == null) {
                                    field.onChange(null);
                                } else {
                                    field.onChange(data.id);
                                }
                            }
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                slotProps={{ inputLabel: { shrink: true, inputMode: 'text' } }}
                                label={noun.toUpperCase()}
                                inputRef={ref}
                            />
                        )}
                    />
                    {errors[fieldName]?.message == null ? null : (
                        <FormHelperText error>
                            <ErrorWidget
                                id={`entity-${fieldName}-error`}
                                errorMessage={<>{errors[fieldName].message}</>}
                            />
                        </FormHelperText>
                    )}
                </div>
            )}
        />
    );
}
